import { PythonModule } from './types';
import { UnifiedQuizContent } from '@/types/unifiedQuizTypes';

export function enhancePythonModuleWithQuiz(module: PythonModule): PythonModule {
  if (module.quiz && module.quiz.questions && module.quiz.questions.length > 0) {
    // Module already has quiz content, just ensure it's properly structured
    return {
      ...module,
      quiz: {
        ...module.quiz,
        questions: module.quiz.questions.map((q, idx) => ({
          ...q,
          id: q.id || `${module.id}-q-${idx + 1}`,
          difficulty: (q as any).difficulty || 'medium',
          explanation: (q as any).explanation || `This question tests your understanding of ${module.title}.`,
          points: (q as any).points || 10,
          category: (q as any).category || 'general'
        }))
      }
    };
  }

  // Generate quiz content based on module content
  const quiz = generatePythonQuizForModule(module);
  
  return {
    ...module,
    quiz: {
      questions: quiz.questions.map(q => ({
        id: q.id,
        question: q.question,
        options: q.options,
        correctAnswer: q.correctAnswer,
        explanation: q.explanation || `This question tests your understanding of ${module.title}.`
      }))
    }
  };
}

export function enhanceAllPythonModulesWithQuizzes(modules: PythonModule[]): PythonModule[] {
  return modules.map(enhancePythonModuleWithQuiz);
}

function generatePythonQuizForModule(module: PythonModule): UnifiedQuizContent {
  const baseQuestions = generatePythonBaseQuestions(module);
  
  // Create difficulty-based questions
  const questionsByDifficulty = {
    easy: generatePythonEasyQuestions(module),
    medium: baseQuestions,
    hard: generatePythonHardQuestions(module)
  };

  return {
    questions: baseQuestions,
    questionsByDifficulty,
    passThreshold: 80,
    category: 'python',
    totalPoints: baseQuestions.reduce((sum, q) => sum + (q.points || 10), 0)
  };
}

function generatePythonBaseQuestions(module: PythonModule) {
  const moduleTitle = module.title.toLowerCase();
  
  if (moduleTitle.includes('variable') || moduleTitle.includes('basic')) {
    return [
      {
        id: `${module.id}-q1`,
        question: 'Which of the following is the correct way to create a variable in Python?',
        options: [
          'var x = 5',
          'x = 5',
          'int x = 5',
          'declare x = 5'
        ],
        correctAnswer: 1,
        difficulty: 'easy' as const,
        explanation: 'In Python, variables are created by simply assigning a value using the = operator.',
        category: 'variables',
        points: 10
      },
      {
        id: `${module.id}-q2`,
        question: 'What data type would the variable x be after this assignment: x = "Hello"?',
        options: ['int', 'float', 'str', 'bool'],
        correctAnswer: 2,
        difficulty: 'medium' as const,
        explanation: 'Strings in Python are represented by the str data type.',
        category: 'data-types',
        points: 15
      }
    ];
  }
  
  if (moduleTitle.includes('function')) {
    return [
      {
        id: `${module.id}-q1`,
        question: 'How do you define a function in Python?',
        options: [
          'function myFunc():',
          'def myFunc():',
          'create myFunc():',
          'func myFunc():'
        ],
        correctAnswer: 1,
        difficulty: 'easy' as const,
        explanation: 'Functions in Python are defined using the "def" keyword.',
        category: 'functions',
        points: 10
      },
      {
        id: `${module.id}-q2`,
        question: 'What keyword is used to return a value from a function?',
        options: ['give', 'return', 'output', 'send'],
        correctAnswer: 1,
        difficulty: 'medium' as const,
        explanation: 'The "return" keyword is used to return values from functions.',
        category: 'functions',
        points: 15
      }
    ];
  }
  
  if (moduleTitle.includes('loop') || moduleTitle.includes('control')) {
    return [
      {
        id: `${module.id}-q1`,
        question: 'Which loop is used to iterate over a sequence in Python?',
        options: ['while', 'for', 'do-while', 'repeat'],
        correctAnswer: 1,
        difficulty: 'medium' as const,
        explanation: 'The "for" loop is commonly used to iterate over sequences like lists and strings.',
        category: 'loops',
        points: 15
      }
    ];
  }
  
  // Default questions for any Python module
  return [
    {
      id: `${module.id}-q1`,
      question: `What is a key concept in ${module.title}?`,
      options: [
        'Understanding Python fundamentals',
        'Memorizing syntax only',
        'Avoiding practical application',
        'Ignoring best practices'
      ],
      correctAnswer: 0,
      difficulty: 'medium' as const,
      explanation: `${module.title} focuses on understanding core Python concepts and their practical applications.`,
      category: 'general',
      points: 10
    }
  ];
}

function generatePythonEasyQuestions(module: PythonModule) {
  return [
    {
      id: `${module.id}-easy-q1`,
      question: 'Python is which type of programming language?',
      options: [
        'Compiled',
        'Interpreted',
        'Assembly',
        'Machine'
      ],
      correctAnswer: 1,
      difficulty: 'easy' as const,
      explanation: 'Python is an interpreted language, executed line by line.',
      category: 'fundamentals',
      points: 5
    }
  ];
}

function generatePythonHardQuestions(module: PythonModule) {
  return [
    {
      id: `${module.id}-hard-q1`,
      question: `Advanced application of concepts from ${module.title}:`,
      options: [
        'Complex implementation patterns',
        'Basic syntax memorization',
        'Avoiding optimization',
        'Ignoring error handling'
      ],
      correctAnswer: 0,
      difficulty: 'hard' as const,
      explanation: `Advanced ${module.title} involves understanding complex implementation patterns and optimization techniques.`,
      category: 'advanced',
      points: 20
    }
  ];
}
